<template>
    <div class="wrapper wrapper--no-max-width">
        <loader v-if="isLoading" />

        <aml-questionnaire v-else-if="shouldDisplayAMLQuestionnaire" />
        <turkish-questionary v-else-if="isAppBlockedByTurkishRequirements && !isTurkishQuestionaryAccepted" />
        <template v-else>
            <header class="row__header">
                <h1>{{ $t(`profile.kyc.level2Text`) }}</h1>
            </header>

            <div class="stepper-container">
                <stepper ref="stepper"
                    :disable="isFormDisabled"
                    :steps="steps"
                    :initialData="form"
                    @perfomAction="onPerfomAction"
                    @finished="finishProcess"
                />
            </div>
        </template>
    </div>
</template>

<script>
// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

import {
    api,
} from '@shared/setups/api';

// Local modules
import localStoreTypes from '@webapp/store/types.js';

// Shared modules
import sharedEpicTypes from '@shared/epics/types.js';
import sharedStoreTypes from '@shared/store/types.js';

// Components
import stepper from '@webapp/components/stepper/stepper.vue';
import stepOneDetails from '@webapp/components/kyc/level-two/steps/step-one-details.vue';
import stepTwoDocument from '@webapp/components/kyc/level-two/steps/step-two-document.vue';
import stepThree from '@webapp/components/kyc/level-two/steps/step-three.vue';
import amlQuestionnaire from '@webapp/modules/customer-first/components/aml-questionnaire/aml-questionnaire.vue';
import turkishQuestionary from '@webapp/modules/customer-first/components/turkish-questionary.vue';
import loader from '@webapp/components/loading-spinner/loading-spinner.vue';

// Types
import {
    JurisdictionRequirement,
} from '@shared/types/jurisdiction';

// Helpers
import globalMixin from '@shared/mixins/global-mixin.js';
import {
    KycLevel2Method,
} from '@webapp/helpers/kyc';
import {
    formatCountry,
} from '@webapp/helpers/country';

import {
    ErrorCode,
} from '@shared/types/kyc';

const USER_DETAILS_STEP = 0;
const INFORMATION_DOCUMENTS_STEP = 1;

export default {
    name: 'kyc-level-two',
    components: {
        stepper,
        amlQuestionnaire,
        turkishQuestionary,
        loader,
    },
    mixins: [globalMixin],
    data() {
        return {
            isProcessing: false,
            steps: [
                {
                    name: this.$t('kycLevel2WebManualSteps.step1'),
                    component: stepOneDetails,
                    requiresActionToContinue: true,
                },
                {
                    name: this.$t('kycLevel2WebManualSteps.step2'),
                    component: stepTwoDocument,
                    requiresActionToContinue: true,
                },
                {
                    name: this.$t('kycLevel2WebManualSteps.step3'),
                    component: stepThree,
                },
            ],
            isLoading: true,
            form: {
                firstName: '',
                lastName: '',
                dateOfBirth: '',
                nationality: null,
                codiceFiscale: '',
                address: null,
                documentType: null,
                documentNumber: '',
                expiryDate: '',
                doesNotExpire: false,
                issuingCountry: null,
                images: [],
                checkboxes: [
                    {
                        text: this.$t('kycLevel2PhotoUploadConfirmation.option1'),
                        value: false,
                    },
                    {
                        text: this.$t('kycLevel2PhotoUploadConfirmation.option2'),
                        value: false,
                    },
                    {
                        text: this.$t('kycLevel2PhotoUploadConfirmation.option3'),
                        value: false,
                    },
                ],
            },
            stepActions: {},
        };
    },
    computed: {
        ...mapGetters([
            'userDetails',
            'hasUpdatedUserDetails',
            'userAddress',
            'hasUpdatedUserAddress',
            'isUserAddressFetched',
            'kycTierLevels',
            'hasUpdatedKycLevelVerification',
            'getLatestValidationError',
            'getLatestNonProcessableError',
            'getLatestError',
            'reverifyKyc',
            'isPartnerApp',
            'userAMLQuestionnaire',
            'kycLevel2ExpirationStatus',
            'isItalianUser',
        ]),

        ...mapGetters('countries', ['countriesList']),

        ...mapGetters('kyc', ['kycLevel2UsedFlow']),

        ...mapGetters('turkishRequirements', ['isTurkishQuestionaryAccepted', 'isAppBlockedByTurkishRequirements']),

        isFormDisabled() {
            if (this.reverifyKyc) {
                return false;
            }

            const level = this.kycTierLevels.find((item) => item.tierLevel === 2);

            return level && (level.status === 'processing' ||
                (level.status === 'validated' && !this.kycLevel2ExpirationStatus) ||
                this.isProcessing);
        },

        shouldDisplayAMLQuestionnaire() {
            return !this.userAMLQuestionnaire && (this.isAppBlockedByTurkishRequirements || !this.isFormDisabled);
        },
    },
    watch: {
        userAddress: 'onInitUserAddress',
        hasUpdatedKycLevelVerification: 'onUpdatedKycLevelVerification',
        getLatestValidationError: 'onValidationError',
        getLatestError: 'onError',
        getLatestNonProcessableError: 'onNonProcessableError',
        userAMLQuestionnaire: 'finishLoading',
        hasUpdatedUserAddress: 'onUpdatedUserAddress',
        hasUpdatedUserDetails: 'onUpdatedUserDetails',
        stepActions: {
            deep: true,
            handler(value) {
                this.onUpdatedStepActions(value);
            },
        },
    },
    destroyed() {
        this.setReverifyKyc(false);
    },
    async created() {
        await this.getKycDocumentTypes({
            tierLevel: 2,
        });

        await this.getUserDetails();

        this.initUserDetails();

        if (this.isUserAddressFetched) {
            this.onInitUserAddress(this.userAddress);
        } else {
            await this.getUserAddress();
        }

        this.updateKycLevelVerification(false);

        if (this.isFormDisabled && !this.isAppBlockedByTurkishRequirements) {
            this.finishLoading();
        } else {
            this.getUserAMLQuestionnaire();
        }
    },
    methods: {
        ...mapActions({
            getUserDetails: sharedEpicTypes.GET_USER_DETAILS,
            updateUserDetails: sharedEpicTypes.UPDATE_USER_DETAILS,
            getUserAddress: sharedEpicTypes.GET_USER_ADDRESS,
            addKycDocumentType: sharedStoreTypes.SET_KYC_DOCUMENT_TYPE,
            setKycDocumentData: sharedStoreTypes.SET_KYC_DOCUMENT_DATA,
            updateUserAddress: sharedEpicTypes.UPDATE_USER_ADDRESS,
            showAppLoader: localStoreTypes.SET_APP_LOADER,
            getKycDocumentTypes: sharedEpicTypes.GET_KYC_DOCUMENTS_TYPES,
            addKycLevelValidation: sharedEpicTypes.ADD_KYC_LEVEL_VERIFICATION,
            setReverifyKyc: localStoreTypes.SET_REVERIFY_KYC,
            setModal: localStoreTypes.SET_MODAL,
            getUserAMLQuestionnaire: sharedStoreTypes.GET_USER_AML_QUESTIONNAIRE,
            updateKycLevelVerification: sharedStoreTypes.UPDATED_KYC_LEVEL_VERIFICATION,
        }),

        /**
         * On update user address
         *
         * @param {boolean} value On update step actions
         * @returns {void}
         */
        onUpdatedStepActions(value) {
            if (Object.keys(value).length && Object.values(value).every((action) => action)) {
                this.showAppLoader({
                    isVisible: false,
                });

                this.$refs.stepper.proceedToNextStep();
                this.stepActions = {};
            }
        },

        /**
         * On update user address
         *
         * @param {boolean} value On update user details
         * @returns {void}
         */
        onUpdatedUserDetails(value) {
            if (value) {
                this.stepActions.updatedUserDetails = true;
            }
        },

        /**
         * On update user address
         *
         * @param {boolean} value On update user address
         * @returns {void}
         */
        onUpdatedUserAddress(value) {
            if (value) {
                this.stepActions.updatedUserAddress = true;
            }
        },

        /**
         * Init user address
         *
         * @returns {void}
         */
        initUserDetails() {
            this.form.firstName = this.userDetails.firstName;
            this.form.lastName = this.userDetails.lastName;

            if (this.userDetails.birthDate) {
                this.form.dateOfBirth = this.userDetails.birthDate;
            }
            this.form.nationality = formatCountry(this.userDetails.nationalityCountryCode);
        },

        /**
         * On init user address
         *
         * @param {object} address The address details to be set on init
         * @returns {void}
         */
        onInitUserAddress(address) {
            this.form.address = formatCountry(address.countryCode);
        },

        /**
         * On api error
         *
         * @param {object} error The object received from api
         * @returns {void}
         */
        onError(error) {
            if (!error.errors.length) {
                return;
            }

            const fetchErrors = this.utilGlobalHandleErrorMessages(error);

            this.$notify({
                type: 'error',
                text: fetchErrors.message,
            });

            this.finishLoading();
        },

        /**
         * On api validation
         *
         * @param {object} error The object received from api
         * @returns {void}
         */
        onValidationError(error) {
            if (Object.keys(error).length) {
                const {
                    firstName = '',
                    lastName = '',
                    birthDate = '',
                    nationalityCountryCode = '',
                    countryCode = '',
                    httpException = '',
                    user = '',
                    code = '',
                } = error;

                if (code === ErrorCode.BlockedCountry) {
                    this.setModal({
                        isVisible: true,
                        isClosable: false,
                        image: require('@webapp/assets/statuses/denied.webp'),
                        title: this.$t('errorMessages.countryRestrictionTitle'),
                        messages: [
                            {
                                text: error.default,
                            },
                        ],
                        acceptButton: {
                            text: this.$t('global.button.understand'),
                            action: () => {
                                this.$router.go();
                                this.resetModal();
                            },
                        },
                    });
                } else {
                    this.$notify({
                        type: 'error',
                        text: firstName || lastName || birthDate || nationalityCountryCode || countryCode || httpException || user,
                    });
                }
            }

            this.showAppLoader({
                isVisible: false,
            });
        },

        /**
         * On api non processable error
         *
         * @param {object} error The object received from api
         * @returns {void}
         */
        onNonProcessableError(error) {
            if (!error.errors.length) {
                return;
            }

            this.finishLoading();
        },

        /**
         * On updated kyc level verification
         *
         * @param {boolean} newValue The new value received
         * @returns {void}
         */
        async onUpdatedKycLevelVerification(newValue) {
            if (newValue) {
                this.isProcessing = true;

                this.showAppLoader({
                    isVisible: false,
                });

                this.showSuccessModal();
                await this.getUserDetails();

                this.$refs.stepper.navigateToStep(0);
            }
        },

        /**
         * Shows success modal
         *
         * @returns {void}
         */
        showSuccessModal() {
            this.setModal({
                isVisible: true,
                componentToLoad: 'modules/customer-first/components/kyc/jumio-result-modal',
                data: {
                    success: true,
                },
            });
        },

        /**
         * Returns if user details have changed
         *
         * @param {object} form the kyc form
         * @returns {boolean} if user details have changed
         */
        haveUserDetailsChanged(form) {
            return form.firstName !== this.userDetails.firstName ||
                form.lastName !== this.userDetails.lastName ||
                form.dateOfBirth !== this.userDetails.birthDate ||
                form.nationality.iso2 !== this.userDetails.nationalityCountryCode;
        },

        /**
         * Returns if user address has changed
         *
         * @param {object} form the kyc form
         * @returns {boolean} if user address has changedd
         */
        hasUserAddressChanged(form) {
            return form.address.iso2 !== this.userAddress.countryCode;
        },

        /**
         * Returns formatted documents
         *
         * @param {object} form the kyc form
         * @returns {object} formatted documents
         */
        formatDocumentsToUpload(form) {
            const docs = form.images;

            return docs.map((document) => {
                document.documentType = form.documentType.value;

                return document;
            });
        },

        onPerfomAction(event) {
            const {
                step, data,
            } = event;

            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            if (step === USER_DETAILS_STEP) {
                this.onUserDetailsStepDone(data);
            } else if (step === INFORMATION_DOCUMENTS_STEP) {
                this.onInformationDocumentsStepDone(data);
            }
        },

        async onUserDetailsStepDone(stepData) {
            this.stepActions = {
                updatedUserDetails: !this.haveUserDetailsChanged(stepData),
                updatedUserAddress: !this.hasUserAddressChanged(stepData),
            };

            if (this.haveUserDetailsChanged(stepData)) {
                await this.updateUserDetails({
                    firstName: stepData.firstName,
                    lastName: stepData.lastName,
                    birthDate: stepData.dateOfBirth,
                    nationalityCountryCode: stepData.nationality.iso2,
                });
            }

            if (this.hasUserAddressChanged(stepData)) {
                await this.updateUserAddress({
                    countryCode: stepData.address.iso2,
                });
            }
        },

        async onInformationDocumentsStepDone(stepData) {
            if (this.isItalianUser) {
                await api.setUserJurisdictionRequirement({
                    value: stepData.codiceFiscale,
                }, {
                    code: JurisdictionRequirement.CodiceFiscale,
                });
            }
            await this.addKycDocumentType(stepData.documentType);
            await this.setKycDocumentData({
                documentType: stepData.documentType,
                countryOfIssue: stepData.issuingCountry.iso2,
                documentNumber: stepData.documentNumber,
                expiryDate: stepData.expiryDate,
                doesNotExpire: stepData.doesNotExpire,
            });

            this.showAppLoader({
                isVisible: false,
            });
            this.$refs.stepper.proceedToNextStep();
        },

        async finishProcess(form) {
            if (this.kycLevel2UsedFlow === KycLevel2Method.Jumio) {
                this.setReverifyKyc(false);

                return;
            }

            await this.submitManualFlow(form);
        },

        /**
         * Submit current form with current form data
         *
         * @param {object} form The new value received
         * @returns {void}
         */
        async submitManualFlow(form) {
            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            await this.addKycLevelValidation({
                tierLevel: 2,
                documents: this.formatDocumentsToUpload(form),
                issuingCountryCode: form.issuingCountry.iso2,
                expiryDate: form.expiryDate,
                doesNotExpire: form.doesNotExpire,
                idNumber: form.documentNumber,
            });

            this.setReverifyKyc(false);
        },

        /**
         * Set loader to false
         *
         * @returns {void}
         */
        finishLoading() {
            this.isLoading = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.stepper-container {
    padding: 5.4rem 2rem;
}
</style>